import type {
  ProjectContextDocumentKind,
  ProjectContextDocumentWriteResult,
  ProjectContextFileEntry,
  ProjectContextOp,
  ProjectContextPatchResult,
  ProjectContextSelfImportResult,
  ProjectContextView,
  ProjectPersonaGenerationStatus,
} from "schemas/bedrockApi/projectContext";
import type { ProjectContextExtractResult } from "schemas/bedrockApi/projectContextExtract";
import type { ProjectContextExtractJob } from "schemas/generated/bedrockApi/projectContextExtract";
import type { BrandPregenStatus } from "schemas/generated/brand";
import type {
  ProjectContextTag,
  ProjectPersonaView,
} from "schemas/projectContext";
import type { RequestFn } from "./request";

/**
 * The project context: the brand rows, brand ops for editors and agents (live
 * on return), context-file pointers. Replaces reading the brand files.
 * Invalid values surface as a `BedrockApiRequestError` with status 422.
 */
export function createContextClient({ request }: { request: RequestFn }) {
  return {
    /** Null when the project has no context rows yet (pre-migration project). */
    get: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectContextView | null> => {
      return request<ProjectContextView | null>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/context`,
      });
    },
    /** Start the lazy migration of this project's legacy brand files into rows; poll `get` until it answers. */
    importSelf: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectContextSelfImportResult> => {
      return request<ProjectContextSelfImportResult>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/import-self`,
      });
    },
    /** Apply brand operations in one transaction; the brand is live when this returns. */
    patch: ({
      projectId,
      ops,
    }: {
      projectId: string;
      ops: ProjectContextOp[];
    }): Promise<ProjectContextPatchResult> => {
      return request<ProjectContextPatchResult>({
        method: "PATCH",
        path: `/api/v1/projects/${projectId}/context/brand`,
        body: { ops },
      });
    },
    /** Writes BUSINESS.md or DESIGN.md from the brand into the team folder and tags it; 409 once it exists, 404 without a brand. */
    writeDocument: ({
      projectId,
      kind,
    }: {
      projectId: string;
      kind: ProjectContextDocumentKind;
    }): Promise<ProjectContextDocumentWriteResult> => {
      return request<ProjectContextDocumentWriteResult>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/documents/${kind}`,
      });
    },
    /** Tag a Drive file as brand context; a file outside the team folder is moved there first, and the entry carries its new path. */
    markFile: ({
      projectId,
      agentFileId,
      tag,
    }: {
      projectId: string;
      agentFileId: string;
      tag: ProjectContextTag;
    }): Promise<ProjectContextFileEntry> => {
      return request<ProjectContextFileEntry>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/files`,
        body: { agentFileId, tag },
      });
    },
    /** Delete the project's brand and everything that belonged to it; `deleted` is false when there was no row. */
    delete: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<{ deleted: boolean }> => {
      return request<{ deleted: boolean }>({
        method: "DELETE",
        path: `/api/v1/projects/${projectId}/context`,
      });
    },
    /** Removes one tag from the file; other brand tags it carries stay. */
    unmarkFile: ({
      projectId,
      agentFileId,
      tag,
    }: {
      projectId: string;
      agentFileId: string;
      tag: ProjectContextTag;
    }): Promise<{ removed: boolean }> => {
      return request<{ removed: boolean }>({
        method: "DELETE",
        path: `/api/v1/projects/${projectId}/context/files/${agentFileId}`,
        query: { tag },
      });
    },
    /**
     * Write customer personas from the brand's own contents onto the brand.
     * Returns at once; follow `personasStatus` until the stage is done or failed.
     */
    generatePersonas: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectPersonaGenerationStatus> => {
      return request<ProjectPersonaGenerationStatus>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/personas/generate`,
      });
    },
    /** Stop the run in flight: nothing it read reaches the brand, and a new run may start at once. */
    cancelPersonas: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectPersonaGenerationStatus> => {
      return request<ProjectPersonaGenerationStatus>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/personas/cancel`,
      });
    },
    personasStatus: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectPersonaGenerationStatus> => {
      return request<ProjectPersonaGenerationStatus>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/context/personas/status`,
      });
    },
    /** Re-render one persona's creature with a fresh seed on the persona row. */
    shufflePersonaAvatar: ({
      projectId,
      key,
    }: {
      projectId: string;
      key: string;
    }): Promise<ProjectPersonaView> => {
      return request<ProjectPersonaView>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/personas/${encodeURIComponent(key)}/avatar`,
      });
    },
    /** Starts a server-side site extraction into a brand kit; follow extractStatus, and with apply (default) the kit lands on the brand when done, else in extractResult. */
    extract: ({
      projectId,
      url,
      model,
      apply,
      mode,
      parts,
      confirmed,
    }: {
      projectId: string;
      url: string;
      model?: string;
      apply?: boolean;
      /** replace: the whole kit; swap: named parts in full; fill: only what the brand lacks. */
      mode?: "replace" | "swap" | "fill";
      /** The parts swap and fill write; omitted writes all of them. */
      parts?: ("facts" | "colors" | "fonts" | "logo" | "images")[];
      /** The person confirmed replacing a brand read from a different website. */
      confirmed?: boolean;
    }): Promise<ProjectContextExtractJob> => {
      return request<ProjectContextExtractJob>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/context/extract`,
        body: {
          url,
          ...(model ? { model } : {}),
          ...(apply === undefined ? {} : { apply }),
          ...(mode === undefined ? {} : { mode }),
          ...(parts === undefined ? {} : { parts }),
          ...(confirmed === undefined ? {} : { confirmed }),
        },
      });
    },
    /** The kit the last run without `apply` produced, as brand operations; 404 until it has finished or once it has expired. */
    extractResult: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ProjectContextExtractResult> => {
      return request<ProjectContextExtractResult>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/context/extract/result`,
      });
    },
    /** Progress of the extraction (shared with signup pregen): the brand-pregen stage. */
    extractStatus: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<BrandPregenStatus> => {
      return request<BrandPregenStatus>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/brand-pregen/status`,
      });
    },
  };
}
